import React, { useState, useEffect, useCallback } from 'react';
import { getUnifiedTasks, type UnifiedTaskRow } from '@/services/unifiedTask.service';
import { UnifiedTasksExecutionBoard } from '@/components/unified/UnifiedTasksExecutionBoard';
import { UnifiedTaskSavedViewsBar } from '@/components/unified/UnifiedTaskSavedViewsBar';
import { applySavedViewFilterRecord } from '@/utils/unifiedTaskSavedViewApply';

/**
 * Company-scoped task board focused on work assigned to others or unassigned (manager / lead visibility).
 */
const TeamTasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<UnifiedTaskRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [moduleFilter, setModuleFilter] = useState<string | undefined>(undefined);
  const [taskSearchQ, setTaskSearchQ] = useState('');
  const [taskSearchDebounced, setTaskSearchDebounced] = useState('');
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [teamOthersOnly, setTeamOthersOnly] = useState(true);
  const [taskTypeFilter, setTaskTypeFilter] = useState<string | undefined>(undefined);
  const [sourceDocTypeFilter, setSourceDocTypeFilter] = useState<string | undefined>(undefined);
  const [taskOriginFilter, setTaskOriginFilter] = useState<'all' | 'system' | 'manual'>('all');
  const [dueTodayOnly, setDueTodayOnly] = useState(false);
  const [noDueDateOnly, setNoDueDateOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [openTasksOnly, setOpenTasksOnly] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState<string | undefined>(undefined);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [readyToStart, setReadyToStart] = useState(false);
  const [slaRiskFilter, setSlaRiskFilter] = useState<
    'all' | 'safe' | 'warning' | 'breach' | 'critical' | 'none'
  >('all');
  const [sortBySla, setSortBySla] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setTaskSearchDebounced(taskSearchQ), 400);
    return () => clearTimeout(id);
  }, [taskSearchQ]);

  /** `sync: true` runs a heavy server-side refresh from finance/sales/SCM; use only on explicit Refresh. */
  const fetchTasks = useCallback((opts?: { sync?: boolean }) => {
    setLoading(true);
    getUnifiedTasks({
      sync: opts?.sync === true,
      module: moduleFilter,
      team_others_only: teamOthersOnly || undefined,
      unassigned_only: unassignedOnly || undefined,
      q: taskSearchDebounced.trim() || undefined,
      priority: priorityFilter,
      overdue_only: overdueOnly || undefined,
      task_type: taskTypeFilter,
      source_document_type: sourceDocTypeFilter,
      origin: taskOriginFilter === 'all' ? undefined : taskOriginFilter,
      due_today_only: dueTodayOnly || undefined,
      no_due_date_only: noDueDateOnly || undefined,
      status: statusFilter,
      open_only: openTasksOnly && !statusFilter ? true : undefined,
      sla_risk: slaRiskFilter !== 'all' ? slaRiskFilter : undefined,
      blocked_only: blockedOnly || undefined,
      ready_to_start: readyToStart || undefined,
      sort: sortBySla ? 'sla' : undefined,
      limit: 500,
      offset: 0,
    })
      .then(setTasks)
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [
    moduleFilter,
    teamOthersOnly,
    unassignedOnly,
    taskSearchDebounced,
    taskTypeFilter,
    sourceDocTypeFilter,
    taskOriginFilter,
    dueTodayOnly,
    noDueDateOnly,
    statusFilter,
    openTasksOnly,
    priorityFilter,
    overdueOnly,
    blockedOnly,
    readyToStart,
    slaRiskFilter,
    sortBySla,
  ]);

  const buildTeamFiltersSnapshot = useCallback((): Record<string, unknown> => {
    const filters: Record<string, unknown> = {};
    if (moduleFilter) filters.module = moduleFilter;
    if (statusFilter) filters.status = statusFilter;
    if (priorityFilter) filters.priority = priorityFilter;
    if (taskTypeFilter) filters.task_type = taskTypeFilter;
    if (sourceDocTypeFilter) filters.sdoc = sourceDocTypeFilter;
    if (taskOriginFilter !== 'all') filters.origin = taskOriginFilter;
    const tq = taskSearchDebounced.trim();
    if (tq) filters.tq = tq.slice(0, 200);
    if (unassignedOnly) filters.unassigned = true;
    else if (teamOthersOnly) filters.team = true;
    if (overdueOnly) filters.overdue = true;
    if (dueTodayOnly) filters.due_today = true;
    if (noDueDateOnly) filters.nodue = true;
    if (openTasksOnly) filters.open = true;
    if (blockedOnly) filters.blocked = true;
    if (readyToStart) filters.ready = true;
    if (slaRiskFilter !== 'all') filters.sla = slaRiskFilter;
    if (sortBySla) filters.sla_sort = true;
    return filters;
  }, [
    moduleFilter,
    statusFilter,
    priorityFilter,
    taskTypeFilter,
    sourceDocTypeFilter,
    taskOriginFilter,
    taskSearchDebounced,
    unassignedOnly,
    teamOthersOnly,
    overdueOnly,
    dueTodayOnly,
    noDueDateOnly,
    openTasksOnly,
    blockedOnly,
    readyToStart,
    slaRiskFilter,
    sortBySla,
  ]);

  const applyTeamBoardFilters = useCallback((f: Record<string, unknown>) => {
    applySavedViewFilterRecord(f, {
      setTaskModuleFilter: setModuleFilter,
      setStatusFilter: setStatusFilter,
      setPriorityFilter: setPriorityFilter,
      setTaskTypeFilter: setTaskTypeFilter,
      setSourceDocTypeFilter: setSourceDocTypeFilter,
      setTaskOriginFilter: setTaskOriginFilter,
      setTaskSearchQ: setTaskSearchQ,
      setTaskSearchDebounced: setTaskSearchDebounced,
      setOverdueOnly: setOverdueOnly,
      setDueTodayOnly: setDueTodayOnly,
      setNoDueDateOnly: setNoDueDateOnly,
      setOpenTasksOnly: setOpenTasksOnly,
      setBlockedOnly: setBlockedOnly,
      setReadyToStart: setReadyToStart,
      setSlaRiskFilter: setSlaRiskFilter,
      setSortBySla: setSortBySla,
    });
    if (f.me === true) {
      setUnassignedOnly(false);
      setTeamOthersOnly(true);
    } else if (f.unassigned === true) {
      setUnassignedOnly(true);
      setTeamOthersOnly(false);
    } else if (f.team === true) {
      setUnassignedOnly(false);
      setTeamOthersOnly(true);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return (
    <div>
      <UnifiedTasksExecutionBoard
        tasks={tasks}
        loading={loading}
        onRefresh={() => fetchTasks({ sync: true })}
        boardTitle="Team workboard"
        boardDescription="Tasks in your company that are unassigned or assigned to someone other than you. Use module and search to narrow down. Use Refresh to re-sync tasks from finance, sales, and supply-chain dashboards. Saved views are stored per user and shared across task boards."
        hideAssignedToMeToggle
        moduleFilter={moduleFilter}
        onModuleFilterChange={setModuleFilter}
        searchQuery={taskSearchQ}
        onSearchQueryChange={setTaskSearchQ}
        unassignedOnly={unassignedOnly}
        onUnassignedOnlyChange={(v) => {
          setUnassignedOnly(v);
          if (v) setTeamOthersOnly(false);
        }}
        teamOthersOnly={teamOthersOnly}
        onTeamOthersOnlyChange={(v) => {
          setTeamOthersOnly(v);
          if (v) setUnassignedOnly(false);
        }}
        taskTypeFilter={taskTypeFilter}
        onTaskTypeFilterChange={setTaskTypeFilter}
        sourceDocumentTypeFilter={sourceDocTypeFilter}
        onSourceDocumentTypeFilterChange={setSourceDocTypeFilter}
        taskOriginFilter={taskOriginFilter}
        onTaskOriginFilterChange={setTaskOriginFilter}
        dueTodayOnly={dueTodayOnly}
        onDueTodayOnlyChange={setDueTodayOnly}
        noDueDateOnly={noDueDateOnly}
        onNoDueDateOnlyChange={setNoDueDateOnly}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        openTasksOnly={openTasksOnly}
        onOpenTasksOnlyChange={setOpenTasksOnly}
        priorityFilter={priorityFilter}
        onPriorityFilterChange={setPriorityFilter}
        overdueOnly={overdueOnly}
        onOverdueOnlyChange={setOverdueOnly}
        blockedOnly={blockedOnly}
        onBlockedOnlyChange={setBlockedOnly}
        readyToStart={readyToStart}
        onReadyToStartChange={setReadyToStart}
        slaRiskFilter={slaRiskFilter}
        onSlaRiskFilterChange={setSlaRiskFilter}
        sortBySla={sortBySla}
        onSortBySlaChange={setSortBySla}
        allowManualCreate
        titleExtra={
          <UnifiedTaskSavedViewsBar
            buildFiltersSnapshot={buildTeamFiltersSnapshot}
            applyFiltersSnapshot={applyTeamBoardFilters}
          />
        }
      />
    </div>
  );
};

export default TeamTasksPage;
